/**
 * Request DTOs — every mutating body an endpoint in docs/API.md §4/§5 accepts.
 *
 * Two rules from docs/API.md §1 apply to every schema in this file:
 *
 *  1. No body ever names a target status. Each lifecycle endpoint has its own
 *     route and its own permission; there is no generic `{status: "CLOSED"}`.
 *  2. Actor identity is server-derived. `requesterId`, `authorId`, `actorId`
 *     and `visibility` (when the caller lacks the internal-comment
 *     permission) are never accepted from these bodies, whatever a client
 *     sends — see docs/SECURITY.md §1.
 *
 * Several `min(1)` rules here deliberately mirror a database CHECK
 * constraint in packages/db (named in each comment). Keeping the two in sync
 * is a discipline, not a mechanism — the DB constraint is the actual
 * enforcement point; the zod rule exists only to turn a bad request into a
 * `400 VALIDATION_ERROR` instead of a raw constraint violation reaching the
 * service layer.
 */
import { z } from 'zod';
import { immediateSupportRequestSchema } from './immediate-support.js';
import { ipWhitelistDetailsSchema } from './ip-whitelist.js';
import { cctvRequestCreateSchema } from './cctv-request.js';
import { queryBoolean } from './query-params.js';
import { TICKET_STATUSES } from './ticket-state-machine.js';
import { SLA_STATES, TICKET_SOURCES } from './ticket-dto.js';
import { emailCreationUsersSchema } from './email-creation.js';
import { emailDeletionUsersSchema } from './email-deletion.js';
import { emailIssueDetailsSchema } from './email-issue.js';
/** Trimmed and required to be non-empty after trimming. */
const requiredText = () => z.string().trim().min(1);
// ---------------------------------------------------------------------------
// Resolution and confirmation — the heart of "RESOLVED is not CLOSED"
// ---------------------------------------------------------------------------
/**
 * Mirrors the DB CHECK `ck_resolution_attested` on `ticket_resolutions`:
 * `problem_summary`, `action_taken` and `resolver_confirmation_text` must
 * all be non-empty after trimming. `resolverConfirmationText` is the
 * explicit attestation — a resolution cannot exist without it.
 */
export const resolveTicketDtoSchema = z.object({
    problemSummary: requiredText(),
    // Genuinely optional narrative fields are `.nullish()`: a client may omit
    // them entirely OR send null. Requiring an explicit `null` for "there was no
    // root cause to record" is the kind of friction that gets worked around with
    // an empty string, which then pollutes the resolution history.
    rootCause: z.string().nullish(),
    actionTaken: requiredText(),
    wasTested: z.boolean(),
    testResult: z.string().nullish(),
    followUpRequired: z.boolean().default(false),
    followUpNotes: z.string().nullish(),
    resolutionCategoryId: z.string().uuid(),
    // The attestation is NOT optional. A resolution cannot exist without it —
    // the DB CHECK `ck_resolution_attested` says the same thing independently.
    resolverConfirmationText: requiredText(),
    /**
     * Email Deletion only (hotfix 27): ADDITIONAL closure-email CC addresses.
     * The mandatory CC is never sent by the client — the server reads it from
     * settings. Validated by `normaliseClosureCc` server-side; rejected for any
     * other request type. 25 is the hard body cap; the admin limit is lower.
     */
    ccRecipients: z.array(z.string().max(320)).max(25).optional(),
});
/**
 * The actor must be the requester (checked server-side, not here).
 * `resolutionId` must equal the ticket's `current_resolution_id` under a row
 * lock — a mismatch is a valid, well-formed request that still fails with
 * `409 STALE_RESOLUTION`, so that check also cannot live in this schema.
 *
 * Mirrors the DB CHECK `ck_rejection_has_reason` on
 * `ticket_resolution_verdicts`: a REJECTED verdict must carry a non-empty
 * comment.
 */
export const confirmResolutionDtoSchema = z
    .object({
    resolutionId: z.string().uuid(),
    verdict: z.enum(['CONFIRMED', 'REJECTED']),
    comment: z.string().trim().optional(),
})
    .refine((data) => data.verdict !== 'REJECTED' || Boolean(data.comment && data.comment.length > 0), {
    message: 'comment is required when rejecting a resolution',
    path: ['comment'],
});
export const reopenTicketDtoSchema = z.object({
    reason: requiredText(),
});
/**
 * Administrative override only (`ticket:close:override`, which no
 * technician role holds — docs/API.md §4). An empty or whitespace-only
 * reason is rejected here, and again by the DB CHECK `ck_override_has_reason`
 * on `ticket_closures`, which additionally guarantees this can never be
 * bypassed by a caller that skips this schema.
 */
export const closeTicketDtoSchema = z.object({
    reason: requiredText(),
});
/**
 * `POST /tickets/:id/cancel` (docs/API.md §4). Legal only from a source
 * status the DB's `ticket_status_transitions` table lists a CANCEL row for
 * (every open status up to, but never including, RESOLVED — see
 * `packages/shared/src/ticket-state-machine.ts`). Enforced for both the
 * requester's own-ticket path and the staff path by
 * `TicketWorkflowService.cancel`; this schema only validates shape.
 */
export const cancelTicketDtoSchema = z.object({
    reason: requiredText(),
});
/**
 * `POST /tickets/:id/restore` (docs/API.md §4). Administrative override
 * only — `ticket:admin:restore`, held only by ADMIN — moving a CANCELLED
 * ticket back to TRIAGED. Mirrors `closeTicketDtoSchema`'s reason
 * requirement; the DB's `ADMIN_OVERRIDE_REASON` guard on this transition
 * backstops it independently.
 */
export const restoreTicketDtoSchema = z.object({
    reason: requiredText(),
});
/**
 * `POST /tickets/:id/escalate` (docs/API.md §4). NOT a status transition —
 * see the `ESCALATE` intent's doc comment in `ticket-state-machine.ts`.
 * `toTeamId`/`toTechnicianId` are both optional: an escalation may simply
 * flag the ticket for attention (an audited `ticket_assignments` row with
 * `is_escalation = true` and no reassignment) or hand it to a specific team
 * or technician in the same action. `reason` is always required — unlike
 * `assignTicketDtoSchema`, where it is optional, an escalation without a
 * stated reason is not useful history.
 */
export const escalateTicketDtoSchema = z.object({
    toTeamId: z.string().uuid().optional(),
    toTechnicianId: z.string().uuid().optional(),
    reason: requiredText(),
});
// ---------------------------------------------------------------------------
// Assignment, triage, pending
// ---------------------------------------------------------------------------
export const assignTicketDtoSchema = z
    .object({
    teamId: z.string().uuid().optional(),
    /** The LEAD technician (the one `assignedTechnician` shows; reports and SLA key off it). */
    technicianId: z.string().uuid().optional(),
    /**
     * Additional (co-assigned) technicians, REPLACING the current co-assignee set.
     * Omitted = leave co-assignees unchanged. Must not contain the lead.
     */
    additionalTechnicianIds: z.array(z.string().uuid()).max(10).optional(),
    reason: z.string().trim().min(1).optional(),
})
    .refine((data) => data.teamId !== undefined || data.technicianId !== undefined, {
    message: 'at least one of teamId or technicianId is required',
    path: ['teamId'],
});
export const triageTicketDtoSchema = z.object({
    categoryId: z.string().uuid(),
    subcategoryId: z.string().uuid().nullable().optional(),
    priorityId: z.string().uuid(),
    impactId: z.string().uuid(),
    urgencyId: z.string().uuid(),
});
export const pendTicketDtoSchema = z.object({
    kind: z.enum(['USER', 'EXTERNAL']),
    reason: requiredText(),
});
// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------
/**
 * `visibility: "INTERNAL"` from a caller without
 * `ticket:comment:create:internal` is rejected server-side, not silently
 * downgraded to PUBLIC — docs/API.md §5. This schema only validates shape;
 * it is not the authorization check.
 */
export const createCommentDtoSchema = z.object({
    body: requiredText(),
    visibility: z.enum(['PUBLIC', 'INTERNAL']),
});
/**
 * `requesterId` / `requesterEmailSnapshot` / `requesterDepartmentId` /
 * `requesterLocationId` are deliberately NOT fields on this schema. The
 * requester is always the authenticated actor, derived server-side from the
 * session — docs/SECURITY.md §1. A body that includes a `requesterId` is
 * ignored by the handler, not merely overridden; it must never reach this
 * validated shape in the first place.
 */
export const createTicketDtoSchema = z.object({
    title: z.string().trim().min(1).max(300),
    description: requiredText(),
    categoryId: z.string().uuid().optional(),
    subcategoryId: z.string().uuid().optional(),
    priorityId: z.string().uuid().optional(),
    impactId: z.string().uuid().optional(),
    urgencyId: z.string().uuid().optional(),
    departmentId: z.string().uuid().optional(),
    locationId: z.string().uuid().optional(),
    /**
     * Employees whose mailboxes are requested. Required (>= 1) when the category is
     * Email Creation and rejected for any other category — enforced in
     * `CreateTicketService`, which knows the category's code.
     */
    emailCreationUsers: emailCreationUsersSchema.optional(),
    emailDeletionUsers: emailDeletionUsersSchema.optional(),
    /**
     * Email Issues details (affected mailbox + type-specific fields). Required when the
     * category is Email Issues and rejected for any other category — enforced in
     * `CreateTicketService`, which knows the category and problem type codes.
     */
    emailIssueDetails: emailIssueDetailsSchema.optional(),
    /**
     * Hotfix 34: "Need immediate support now" (standard form). The API re-checks the
     * working-hours window with server time; outside it the request is refused with
     * field `immediateSupport` / issue `immediate_support_unavailable`.
     */
    immediateSupport: immediateSupportRequestSchema.optional(),
    /** Hotfix 34: IP Whitelisting[TV/PC] only (device type = the ticket's Type). */
    ipWhitelistDetails: ipWhitelistDetailsSchema.optional(),
    /** Hotfix 34: CCTV Footage & Live Access only (requester sections A-C + approver ref). */
    cctvRequest: cctvRequestCreateSchema.optional(),
});
// ---------------------------------------------------------------------------
// Listing
// ---------------------------------------------------------------------------
/**
 * The sort field is an explicit allowlist and is never passed through to
 * SQL as a raw column name — docs/API.md §3 / docs/SECURITY.md. Widening
 * this list is a conscious, reviewed change, not a side effect of adding a
 * column somewhere else.
 */
export const TICKET_LIST_SORT_FIELDS = ['createdAt', 'updatedAt', 'priority', 'resolutionDueAt'];
/**
 * A repeated query key (`?status=A&status=B`) arrives as an array, but a
 * single one (`?status=A`) arrives as a plain string from Fastify's parser.
 * Both are the same request; normalise to an array before validating.
 */
const oneOrMany = (item) => z.preprocess((v) => (v === undefined || Array.isArray(v) ? v : [v]), z.array(item));
/** `'me'` resolves to the calling actor server-side (ticket-query.service). */
const uuidOrMe = z.union([z.literal('me'), z.string().uuid()]);
export const ticketListQueryDtoSchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    sort: z.enum(TICKET_LIST_SORT_FIELDS).default('createdAt'),
    order: z.enum(['asc', 'desc']).default('desc'),
    status: oneOrMany(z.enum(TICKET_STATUSES)).optional(),
    priorityId: z.string().uuid().optional(),
    categoryId: z.string().uuid().optional(),
    subcategoryId: z.string().uuid().optional(),
    impactId: z.string().uuid().optional(),
    urgencyId: z.string().uuid().optional(),
    teamId: z.string().uuid().optional(),
    /**
     * "No team assigned" / "no technician assigned" — the queries the
     * "Unassigned" queue actually needs. A NEW ticket has no team by design
     * (a team is assigned at triage), so before these existed the only way to
     * find one was to fetch a page and filter client-side, which silently
     * misses anything past the first page. `true` matches only a null
     * assignment; `false` matches only an actual one; omitted applies no
     * filter on the field at all. Independent of `teamId` / `technicianId`
     * below — combining, e.g., `teamId` with `unassignedTeam: true` is
     * self-contradictory and simply yields zero rows, not a validation error.
     */
    unassignedTeam: queryBoolean.optional(),
    unassignedTechnician: queryBoolean.optional(),
    technicianId: uuidOrMe.optional(),
    requesterId: uuidOrMe.optional(),
    departmentId: z.string().uuid().optional(),
    locationId: z.string().uuid().optional(),
    source: z.enum(TICKET_SOURCES).optional(),
    slaState: z.enum(SLA_STATES).optional(),
    ticketNumber: z.string().optional(),
    reopened: queryBoolean.optional(),
    unverifiedSender: queryBoolean.optional(),
    createdFrom: z.coerce.date().optional(),
    createdTo: z.coerce.date().optional(),
    updatedFrom: z.coerce.date().optional(),
    updatedTo: z.coerce.date().optional(),
    q: z.string().trim().min(1).optional(),
});
// ---------------------------------------------------------------------------
// Audit trail — GET /tickets/:id/timeline and GET /tickets/:id/audit
// ---------------------------------------------------------------------------
/**
 * Cursor (keyset) pagination for both audit-trail reads, same reasoning as
 * `ticketListQueryDtoSchema` above. `cursor` is the decimal string form of
 * the last `ticket_audit_log.id` (a bigint) seen on the previous page — it is
 * validated as digits-only here so a malformed cursor is a `400` from zod
 * rather than a `BigInt()` throw reaching the repository.
 */
export const auditTrailQueryDtoSchema = z.object({
    cursor: z.string().regex(/^\d+$/, 'cursor must be a decimal audit log id').optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
//# sourceMappingURL=ticket-commands.js.map